import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import hljs from "highlight.js/lib/core";
import hljsJson from "highlight.js/lib/languages/json";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SaveStatus } from "../../shared/utility/save-status";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import KuikaButton, { type KuikaButtonStatus } from "../ui-kit/kuika-button/kuika-button";
import { copyTextToClipboard } from "../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import {
  CellValueKind,
  classifyValue,
  hasSuspiciousWhitespace,
  IJsonProbe,
  ITextStats,
  IValidation,
  renderInvisibles,
  statsFor,
  tryParseJson,
  validateAgainstColumn
} from "./cell-expander-helpers";

// Register only the JSON language so we don't pay the cost of every
// grammar in the highlight.js bundle. Safe to call multiple times —
// hljs no-ops on re-registration.
hljs.registerLanguage("json", hljsJson);

// Beyond this size, syntax highlighting is more disruptive than
// useful — every keystroke runs the JSON tokenizer over the whole
// string and the editor starts to drop frames. The user gets the
// plain textarea instead; JSON Pretty / Minify still work, just
// without colorisation.
const JSON_HIGHLIGHT_MAX_CHARS: number = 50_000;

// KOA-5857 — Expanded text editor for String cells.
//
// Triggered from the per-cell "Expand" adornment, replaces the inline
// 32 px input with a multi-line textarea + value-state inspector +
// inline validation + original-value comparison + text tools. JSON
// content gets syntax-highlighted via an underlay; whitespace can be
// visualised via the same underlay when the user asks.
//
// State legibility: distinct NULL / empty / whitespace-only / value
// lanes. The textarea can't express NULL on its own (it only holds
// strings), so we hold an explicit `isNull` flag alongside the text.

export interface ICellExpanderModalProps {
  isOpen: boolean;
  column: IDatabaseColumnModel;
  initialValue: unknown;
  onClose: () => void;
  onApply: (next: string | null) => void;
}

// Segmented cluster button — a tight `<button>` styled to slot into a
// `.kuika-cell-expander-cluster` shared-border container. Internal
// 1 px hairlines between siblings give the row formula-bar / segmented-
// control character without each button needing its own outline. The
// glyph sits to the left of the label in a muted weight, adding
// recognisability without going icon-only.
interface IClusterButtonProps {
  glyph: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  // `aria-pressed` for toggle-style buttons (Show / Hide invisibles).
  // Undefined = not a toggle.
  pressed?: boolean;
  title?: string;
}

const ClusterButton: React.FC<IClusterButtonProps> = (props: IClusterButtonProps): JSX.Element => {
  const { glyph, label, onClick, disabled, pressed, title } = props;
  return (
    <button
      type="button"
      className={
        "kuika-cell-expander-cluster__button" +
        (pressed === true ? " kuika-cell-expander-cluster__button--pressed" : "")
      }
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      title={title}
    >
      <span className="kuika-cell-expander-cluster__glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="kuika-cell-expander-cluster__label">{label}</span>
    </button>
  );
};

interface IColumnMetaProps {
  column: IDatabaseColumnModel;
}

const ColumnMetaPills: React.FC<IColumnMetaProps> = ({ column }: IColumnMetaProps): JSX.Element => {
  const max: number | null | undefined = column.maxLength;
  return (
    <span className="kuika-table-editor__cell-expander-meta-pills">
      {column.nullable === false ? (
        <span className="kuika-table-editor__cell-expander-pill">required</span>
      ) : (
        <span className="kuika-table-editor__cell-expander-pill">nullable</span>
      )}
      {typeof max === "number" && max > 0 && (
        <span className="kuika-table-editor__cell-expander-pill">max {max.toLocaleString("en-US")}</span>
      )}
    </span>
  );
};

const initialIsNull = (raw: unknown): boolean => raw === null || raw === undefined;

const initialTextOf = (raw: unknown): string => {
  if (raw === null || raw === undefined) {
    return "";
  }
  return String(raw);
};

const CellExpanderModal: React.FC<ICellExpanderModalProps> = (props: ICellExpanderModalProps): JSX.Element | null => {
  const { isOpen, column, initialValue, onClose, onApply } = props;

  const startText: string = initialTextOf(initialValue);
  const startIsNull: boolean = initialIsNull(initialValue);

  const [text, setText] = useState<string>(startText);
  const [isNull, setIsNull] = useState<boolean>(startIsNull);
  const [showInvisibles, setShowInvisibles] = useState<boolean>(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState<boolean>(false);
  // Apply is a local commit (the value goes into the pending edits, not to the server), so it has
  // no loading phase — only the ✓ held long enough to be read before the dialog leaves.
  const [applyStatus, setApplyStatus] = useState<KuikaButtonStatus>("idle");
  const applyMachineRef = useRef<SaveStatus | null>(null);
  if (applyMachineRef.current === null) {
    applyMachineRef.current = new SaveStatus(setApplyStatus);
  }
  const applyMachine: SaveStatus = applyMachineRef.current;
  useEffect((): (() => void) => (): void => applyMachine.dispose(), [applyMachine]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLPreElement | null>(null);
  const keepEditingButtonRef = useRef<HTMLButtonElement | null>(null);

  // When the dirty-close confirm appears, move focus to the safe
  // default action ("Keep editing"). The strip is a contextual
  // alert, not a modal — we don't trap focus or block the rest of
  // the modal — but focusing the safe button means an accidental
  // Enter doesn't fire the destructive Discard, and AT users land
  // on the actionable affordance instead of having to hunt for it.
  useEffect((): void => {
    if (confirmingDiscard) {
      keepEditingButtonRef.current?.focus();
    }
  }, [confirmingDiscard]);

  // Reset local edit state ONLY on the closed → open transition.
  // Previously the deps included `initialValue`, so a background
  // refresh / auto-save / concurrent user update that changed the
  // underlying cell value mid-edit would silently overwrite the
  // user's in-progress typing with the new source value. Apple-bar
  // = the user's typing wins until they explicitly Apply or
  // Discard. The conflict (if any) surfaces on Save via the
  // existing refresh-conflict detection in use-table-editor-
  // mutations.
  const prevOpenRef = useRef<boolean>(false);
  useEffect((): void => {
    if (isOpen && !prevOpenRef.current) {
      setText(startText);
      setIsNull(startIsNull);
      setShowInvisibles(false);
      setConfirmingDiscard(false);
    }
    prevOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const originalStats: ITextStats = useMemo((): ITextStats => statsFor(startText), [startText]);
  const currentStats: ITextStats = useMemo((): ITextStats => statsFor(text), [text]);

  const valueKind: CellValueKind = classifyValue(isNull, text);
  const isModified: boolean = isNull !== startIsNull || (isNull === false && text !== startText);

  const validation: IValidation = useMemo(
    (): IValidation => validateAgainstColumn(column, isNull, text, currentStats),
    [column, isNull, text, currentStats]
  );

  const jsonProbe: IJsonProbe = useMemo(
    (): IJsonProbe => (isNull ? { ok: false } : tryParseJson(text)),
    [text, isNull]
  );
  const isJson: boolean = jsonProbe.ok;

  // Surface the Show-invisibles button only when the content has
  // whitespace worth visualising (tabs, leading / trailing, or runs
  // of 2+ spaces). The toolbar stays quieter on ordinary prose
  // strings — most cells never need this affordance. Once the user
  // toggles it ON, keep showing the button so they have an off-ramp
  // even after the suspicious whitespace is gone (e.g. they hit
  // Trim and now want to flip the view back).
  const showInvisiblesAvailable: boolean = useMemo(
    (): boolean => !isNull && (hasSuspiciousWhitespace(text) || showInvisibles),
    [isNull, text, showInvisibles]
  );

  // Code-like content (JSON) gets monospace. Plain prose strings use
  // the app font — monospace by default was making English text feel
  // like raw output.
  // Monospace also turns on with Show-invisibles. The dot / arrow /
  // pilcrow markers in the overlay rely on `1ch` (the CSS unit
  // defined as the advance width of `0`) matching the width of a
  // real space character. That equivalence only holds in mono
  // fonts; in the default proportional system font `0` is wider
  // than a space, so the markers would push the overlay text out
  // of alignment with the textarea cursor underneath. Switching to
  // mono whenever Show-invisibles is on guarantees character-cell
  // alignment AND signals "you're in a debug view" via the font.
  const useMonospace: boolean = isJson || showInvisibles;

  // Compute the overlay HTML once per text change. JSON highlighting
  // and whitespace visualisation share the underlay — they're mutually
  // exclusive (the user toggles one or the other).
  const overlayHtml: string | null = useMemo((): string | null => {
    if (isNull) {
      return null;
    }
    if (showInvisibles) {
      return renderInvisibles(text);
    }
    if (isJson && text.length <= JSON_HIGHLIGHT_MAX_CHARS) {
      return hljs.highlight(text, { language: "json" }).value;
    }
    return null;
  }, [text, isNull, isJson, showInvisibles]);

  // Sync scroll between textarea and overlay so the highlighted /
  // whitespace-annotated content tracks the user's view position.
  const handleTextareaScroll = (e: React.UIEvent<HTMLTextAreaElement>): void => {
    const overlay: HTMLPreElement | null = overlayRef.current;
    if (overlay === null) {
      return;
    }
    overlay.scrollTop = e.currentTarget.scrollTop;
    overlay.scrollLeft = e.currentTarget.scrollLeft;
  };

  // ── Actions ─────────────────────────────────────────────────────
  const prettifyJson = (): void => {
    if (jsonProbe.ok) {
      setText(JSON.stringify(jsonProbe.value, null, 2));
    }
  };
  const minifyJson = (): void => {
    if (jsonProbe.ok) {
      setText(JSON.stringify(jsonProbe.value));
    }
  };
  // "Start over" gestures all reset the Show-invisibles view
  // preference. Without this, toggling invisibles on, then clicking
  // Set null / Set empty / Revert, leaves the preference held in
  // state: a later edit that types suspicious whitespace would
  // immediately re-mount the cluster in the "Hide invisibles"
  // position, which surprises the user. Resetting on these explicit
  // gestures means the view preference matches the moment the user
  // last set it.
  const setNull = (): void => {
    setIsNull(true);
    setText("");
    setShowInvisibles(false);
  };
  const setEmptyString = (): void => {
    setIsNull(false);
    setText("");
    setShowInvisibles(false);
  };
  const trim = (): void => {
    if (isNull) {
      return;
    }
    setText(text.trim());
  };
  const copyToClipboard = (): void => {
    // The shared write, which falls back to the textarea trick instead of doing nothing when the
    // Clipboard API is missing or refused.
    void copyTextToClipboard(isNull ? "" : text);
  };
  const revert = (): void => {
    setText(startText);
    setIsNull(startIsNull);
    setShowInvisibles(false);
  };
  const apply = (): void => {
    if (validation.error !== null) {
      // Keep focus + selection on the textarea so the user can fix
      // the value without losing their cursor position.
      textareaRef.current?.focus();
      return;
    }
    // A second press during the ✓ would apply the same value twice.
    if (!applyMachine.canStart) return;
    // The cell behind takes the value at once; the ✓ is held on Apply, then the dialog leaves.
    onApply(isNull ? null : text);
    applyMachine.succeed(onClose);
  };

  // Dirty-close — Cancel button, outside click, and Esc all funnel
  // through here. A clean unmodified state closes immediately; a dirty
  // state surfaces an inline confirm so an accidental Esc doesn't
  // throw away the user's edits.
  const requestClose = (): void => {
    if (!isModified) {
      onClose();
      return;
    }
    setConfirmingDiscard(true);
  };
  const confirmDiscard = (): void => {
    setConfirmingDiscard(false);
    onClose();
  };
  const cancelDiscard = (): void => {
    setConfirmingDiscard(false);
    textareaRef.current?.focus();
  };

  if (!isOpen) {
    return null;
  }
  // ID for the validation banner so the textarea's aria-describedby
  // can point at it for screen readers.
  const errorBannerId: string = "kuika-cell-expander-error";
  const warningBannerId: string = "kuika-cell-expander-warning";
  const describedBy: string =
    validation.error !== null ? errorBannerId : validation.warning !== null ? warningBannerId : "";
  return (
    <KModal
      showModal={isOpen}
      closeModal={requestClose}
      header={
        <span className="kuika-table-editor__cell-expander-header">
          <span className="kuika-table-editor__cell-expander-header-kicker" aria-hidden="true">
            Edit value
          </span>
          <span className="kuika-table-editor__cell-expander-header-title">{column.name}</span>
          <ColumnMetaPills column={column} />
        </span>
      }
      width={680}
      showFullScreenButton
      className="kuika-cell-expander-modal"
      footer={
        // KModalFooter — one size, one casing, destructive-on-the-left —
        // used by every dialog on this shell. The value-state actions
        // (Set null / Set empty / Revert) aren't a destructive delete,
        // but they're the dialog's own left-side group, so they ride in
        // `children` next to where a delete button would go.
        <KModalFooter
          onCancel={requestClose}
          submitLabel="Apply"
          onSubmit={apply}
          status={applyStatus}
          statusLabels={{ success: "Applied" }}
          disabled={validation.error !== null}
        >
          <span className="kuika-cell-expander-cluster" role="group" aria-label="Value actions">
            {column.nullable !== false && (
              <ClusterButton glyph="∅" label="Set null" onClick={setNull} disabled={isNull} />
            )}
            <ClusterButton glyph='""' label="Set empty" onClick={setEmptyString} disabled={!isNull && text === ""} />
            <ClusterButton glyph="↺" label="Revert" onClick={revert} disabled={!isModified} />
          </span>
        </KModalFooter>
      }
    >
      <div className="kuika-table-editor__cell-expander">
        {/* Inspector kind-line — one editorial caption above the
            editor, not a stack of utility pills. Diamond glyph
            (◇ unchanged / ◆ modified) carries the modified-state
            signal so the eye picks it up without scanning text. */}
        <div className="kuika-table-editor__cell-expander-strip">
          <span className="kuika-table-editor__cell-expander-strip-state">
            <span
              className={
                "kuika-table-editor__cell-expander-statemark" +
                (isModified ? " kuika-table-editor__cell-expander-statemark--on" : "")
              }
              aria-hidden="true"
            >
              {isModified ? "◆" : "◇"}
            </span>
            <span className="kuika-table-editor__cell-expander-statename">{isModified ? "modified" : "unchanged"}</span>
            {valueKind === "null" && (
              <>
                <span className="kuika-table-editor__cell-expander-sep" aria-hidden="true">
                  ·
                </span>
                <span className="kuika-table-editor__cell-expander-statename">null</span>
              </>
            )}
            {valueKind === "empty" && (
              <>
                <span className="kuika-table-editor__cell-expander-sep" aria-hidden="true">
                  ·
                </span>
                <span className="kuika-table-editor__cell-expander-statename">empty</span>
              </>
            )}
            {valueKind === "whitespace" && (
              <>
                <span className="kuika-table-editor__cell-expander-sep" aria-hidden="true">
                  ·
                </span>
                <span className="kuika-table-editor__cell-expander-statename kuika-table-editor__cell-expander-statename--warn">
                  whitespace only
                </span>
              </>
            )}
            <span className="kuika-table-editor__cell-expander-sep" aria-hidden="true">
              ·
            </span>
            <span className="kuika-table-editor__cell-expander-stats">
              {currentStats.chars.toLocaleString("en-US")} char{currentStats.chars === 1 ? "" : "s"}
              {currentStats.lines > 1 && (
                <>
                  <span className="kuika-table-editor__cell-expander-sep" aria-hidden="true">
                    ·
                  </span>
                  {currentStats.lines.toLocaleString("en-US")} lines
                </>
              )}
              {isModified && (
                <>
                  <span className="kuika-table-editor__cell-expander-sep" aria-hidden="true">
                    ·
                  </span>
                  was {startIsNull ? "null" : `${originalStats.chars.toLocaleString("en-US")} chars`}
                </>
              )}
            </span>
          </span>
          {/* Tools row — segmented clusters with hairline dividers.
              Three logical groups separated by 8 px breathing space:
              JSON formatters | view toggle | text tools. Within a
              cluster, buttons share a single border + are split by
              1 px internal hairlines. Reads as deliberate instrument
              groups, not floating labels. */}
          <span className="kuika-table-editor__cell-expander-strip-tools" role="toolbar" aria-label="Editor tools">
            {isJson && (
              <span className="kuika-cell-expander-cluster" role="group" aria-label="JSON formatting">
                <ClusterButton glyph="⇲" label="Pretty" onClick={prettifyJson} />
                <ClusterButton glyph="⇱" label="Minify" onClick={minifyJson} />
              </span>
            )}
            {showInvisiblesAvailable && (
              <span className="kuika-cell-expander-cluster" role="group" aria-label="View">
                <ClusterButton
                  glyph="¶"
                  label={showInvisibles ? "Hide invisibles" : "Show invisibles"}
                  onClick={(): void => setShowInvisibles(!showInvisibles)}
                  pressed={showInvisibles}
                  title="Show spaces / tabs / line breaks"
                />
              </span>
            )}
            <span className="kuika-cell-expander-cluster" role="group" aria-label="Text tools">
              <ClusterButton glyph="⧉" label="Copy" onClick={copyToClipboard} disabled={isNull && text.length === 0} />
              <ClusterButton
                glyph="⇥"
                label="Trim"
                onClick={trim}
                disabled={isNull || text === text.trim()}
                title="Strip leading and trailing whitespace"
              />
            </span>
          </span>
        </div>

        {/* Editor surface — textarea + optional syntax/whitespace
            underlay. Both layers share the wrapper's font metrics so
            the underlay text aligns with the textarea cursor. */}
        <div
          className="kuika-table-editor__cell-expander-editor"
          data-mono={useMonospace ? "true" : "false"}
          data-overlay={overlayHtml !== null ? "true" : "false"}
        >
          {overlayHtml !== null && (
            <pre
              ref={overlayRef}
              aria-hidden="true"
              className="kuika-table-editor__cell-expander-overlay hljs"
              dangerouslySetInnerHTML={{ __html: overlayHtml + "\n" }}
            />
          )}
          <textarea
            ref={textareaRef}
            className="kuika-table-editor__cell-expander-textarea"
            autoFocus
            spellCheck={false}
            value={isNull ? "" : text}
            placeholder={isNull ? "null — type to set a value" : ""}
            aria-label={`Edit cell value for ${column.name}`}
            aria-invalid={validation.error !== null ? true : undefined}
            aria-describedby={describedBy.length > 0 ? describedBy : undefined}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => {
              if (isNull) {
                setIsNull(false);
              }
              setText(e.target.value);
            }}
            onScroll={handleTextareaScroll}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
              // ⌘/Ctrl+Enter applies. Esc handled by the modal — it
              // calls requestClose which surfaces the dirty-close
              // confirm if there are unsaved edits.
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                apply();
              }
            }}
          />
        </div>

        {/* ── Validation surface ─────────────────────────────────── */}
        {validation.error !== null && (
          <div
            id={errorBannerId}
            className="kuika-table-editor__cell-expander-banner kuika-table-editor__cell-expander-banner--error"
            role="alert"
          >
            {validation.error}
          </div>
        )}
        {validation.error === null && validation.warning !== null && (
          <div
            id={warningBannerId}
            className="kuika-table-editor__cell-expander-banner kuika-table-editor__cell-expander-banner--warn"
            role="status"
          >
            {validation.warning}
          </div>
        )}

        {/* Inline dirty-close confirm. Lives inside the modal so an
            accidental Esc doesn't drop the work; the actual close
            only fires after the user confirms. */}
        {confirmingDiscard && (
          // Inline alert — NOT a true dialog. The strip sits inside
          // the modal body, the rest of the modal stays interactive,
          // and dismissing via Esc / outside-click cancels the
          // confirm instead of trapping focus. `role="alert"` (not
          // `alertdialog`) matches that contract: AT announces the
          // message + the action buttons on appearance, no modality
          // expectation. `aria-labelledby` points at the title so the
          // announcement carries the right framing.
          <div
            id="kuika-cell-expander-confirm"
            className="kuika-table-editor__cell-expander-confirm"
            role="alert"
            aria-labelledby="kuika-cell-expander-confirm-title"
          >
            <span id="kuika-cell-expander-confirm-title" className="kuika-table-editor__cell-expander-confirm-title">
              Discard your edits?
            </span>
            <span className="kuika-table-editor__cell-expander-confirm-spacer" />
            <KuikaButton
              type="text"
              size="sm"
              onClick={cancelDiscard}
              // Reference for the focus-on-show effect — the safe
              // default action receives focus when the strip appears
              // so an accidental Enter doesn't fire Discard.
              ref={keepEditingButtonRef}
            >
              Keep editing
            </KuikaButton>
            <KuikaButton type="primary" size="sm" color="danger" onClick={confirmDiscard}>
              Discard
            </KuikaButton>
          </div>
        )}
      </div>
    </KModal>
  );
};

export default CellExpanderModal;
